import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Login from "./pages/AuthPages/Login";
import SignUp from "./pages/AuthPages/SignUp";
import LandingPage from "./pages/LandingPage";
import Pricing from "./pages/Pricing";
import ResetPassword from "./pages/AuthPages/Reset/ResetPassword";
import CheckReset from "./pages/AuthPages/Reset/CheckReset";
import ChangePassword from "./pages/AuthPages/Reset/ChangePassword";
import Verify from "./pages/AuthPages/Verify/Verify";
import VerifySuccess from "./pages/AuthPages/Verify/VerifySuccess";
import StoreSetup from "./pages/StorePages/StoreSetup";
import UserStores, { getSites } from "./pages/StorePages/UserStores";
import { Toaster } from "sonner";
import { useEffect } from "react";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import NotFound from "./components/NotFound";
// import Courses from "./pages/Courses";
import AuthCallback from "./components/GoogleAuth/AuthCallBack";
import supabase from "./helper/supabaseClient";
import { useDispatch } from "react-redux";
import { setCredentials } from "./features/authSlice";
import { JelloPages } from "./pages/Jellopages";
import { initAppData } from "./features/appSlice";
import { AppDispatch } from "./app/store";
import { trackPageView } from "./utils/posthog";
import Survey from "./pages/Survey";
import JBA from "./pages/JBA/JBA";
import JBAPaymentSuccess from "./pages/JBA/JBAPayment";
import SimpleAnalyticsDemo from "./pages/SimpleAnalyticsDemo";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const searchParams = new URLSearchParams(window.location.search);
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  // handle when user arrives from dashboard
  const access_token = searchParams.get("access_token");
  const refresh_token = searchParams.get("refresh_token");

  useEffect(() => {
    dispatch(initAppData());
    trackPageView(window.location.pathname);
  }, [dispatch]);

  useEffect(() => {
    if (import.meta.env.VITE_ENV === "production") {
      return;
    }

    const existingRobotsMeta = document.querySelector(
      'meta[name="robots"]'
    ) as HTMLMetaElement | null;
    const robotsMeta = existingRobotsMeta || document.createElement("meta");
    const previousContent = robotsMeta.getAttribute("content");

    if (!existingRobotsMeta) {
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }

    robotsMeta.setAttribute("content", "noindex, nofollow");

    return () => {
      if (previousContent) {
        robotsMeta.setAttribute("content", previousContent);
        return;
      }

      if (!existingRobotsMeta) {
        robotsMeta.remove();
      } else {
        robotsMeta.removeAttribute("content");
      }
    };
  }, []);

  useEffect(() => {
    if (access_token && refresh_token) {
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      const setSession = async () => {
        const { data, error } = await supabase.auth.setSession({
          access_token,
          refresh_token,
        });

        if (error) {
          console.log("failed to authenticate");
          navigate("/login");
        }

        if (data?.session && data.user) {
          const mappedUser = {
            id: data.user.id,
            email: data.user.email ?? "", // force string
            first_name: data.user.user_metadata.first_name ?? "",
            last_name: data.user.user_metadata.last_name ?? "",
            last_login: new Date().toISOString(),
            sites: [], // TODO: fetch separately
          };

          dispatch(
            setCredentials({
              user: mappedUser,
              token: data.session.access_token,
              refresh_token: data.session.refresh_token,
            })
          );
        }
      };
      setSession();
    }
  }, [access_token, refresh_token]);

  useEffect(() => {
    // watch for auth events
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "INITIAL_SESSION" && session) {
          window.fbq && window.fbq("track", "CompleteRegistration");
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route index element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/verify-mail" element={<Verify />} />
        <Route path="/verify" element={<VerifySuccess />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/privacy_policy" element={<Privacy />} />
        <Route path="/terms_conditions" element={<Terms />} />
        <Route path="/mail-reset" element={<CheckReset />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<NotFound />} />
        <Route path="/password-reset" element={<ChangePassword />} />
        <Route path="/add-store" element={<StoreSetup />} />
        <Route path="/my-stores" element={<UserStores />} />
        {/* <Route path="/courses" element={<Courses />} /> */}
        <Route path="/jellopages" element={<JelloPages />} />
        <Route path="/jba" element={<JBA />} />
        <Route path="/jba/payment/success" element={<JBAPaymentSuccess />} />
        <Route path="/jba/retention-demo" element={<SimpleAnalyticsDemo />} />
        {/* <Route path="/survey" element={<Survey />} /> */}
        <Route path="/auth/callback" element={<AuthCallback />} />
      </Routes>
      <Toaster richColors position="top-right" />
    </>
  );
}

export default App;

// Declare global functions for TypeScript inference
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}
